import React from 'react';
import { cn, formatNumber, getChannelColor } from '../../lib/utils';
import { Linkedin, Mail, Megaphone, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const channelConfig = {
    linkedin: {
        icon: Linkedin,
        label: 'LinkedIn',
        color: 'linkedin',
        path: '/channels/linkedin',
    },
    email: {
        icon: Mail,
        label: 'Email',
        color: 'email',
        path: '/channels/email',
    },
    ads: {
        icon: Megaphone,
        label: 'Ads',
        color: 'ads',
        path: '/channels/ads',
    },
    inbound: {
        icon: TrendingUp,
        label: 'Inbound',
        color: 'inbound',
        path: '/channels/inbound',
    },
};

export const ChannelTile = ({ 
    channel, 
    metrics, 
    className 
}) => {
    const config = channelConfig[channel];
    if (!config) return null;

    const Icon = config.icon;
    const channelColor = getChannelColor(channel);

    const getMetricItems = () => {
        switch (channel) {
            case 'linkedin':
                return [
                    { label: 'Reachouts', value: metrics?.reachouts_sent || 0 },
                    { label: 'Responses', value: metrics?.responses || 0 },
                    { label: 'MQLs', value: metrics?.mqls || 0 },
                ];
            case 'email':
                return [
                    { label: 'Sent', value: metrics?.emails_sent || 0 },
                    { label: 'Opened', value: metrics?.opened || 0 },
                    { label: 'MQLs', value: metrics?.mqls || 0 },
                ];
            case 'ads':
                return [
                    { label: 'Impressions', value: metrics?.impressions || 0 },
                    { label: 'Clicks', value: metrics?.clicks || 0 },
                    { label: 'CTR', value: `${(metrics?.ctr || 0).toFixed(1)}%` },
                ];
            case 'inbound':
                return [
                    { label: 'Views', value: metrics?.total_views || 0 },
                    { label: 'Engagement', value: metrics?.total_engagement || 0 },
                    { label: 'Leads', value: metrics?.total_leads || 0 },
                ];
            default:
                return [];
        }
    };

    return (
        <Link
            to={config.path}
            className={cn(
                'card-base card-hover p-6 block group',
                className
            )}
            data-testid={`channel-tile-${channel}`}
        >
            <div className="flex items-center justify-between mb-4">
                <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: `${channelColor}20` }}
                >
                    <Icon 
                        className="w-6 h-6"
                        style={{ color: channelColor }}
                    />
                </div>
                <ArrowRight className="w-5 h-5 text-content-muted group-hover:text-brand-red group-hover:translate-x-1 transition-all duration-200" />
            </div>

            <h3 className="text-lg font-heading font-bold text-white mb-4">
                {config.label}
            </h3>

            <div className="space-y-3">
                {getMetricItems().map((item, index) => (
                    <div 
                        key={index}
                        className="flex items-center justify-between"
                    >
                        <span className="text-sm text-content-secondary">
                            {item.label}
                        </span>
                        <span className="text-sm font-bold text-white">
                            {typeof item.value === 'number' ? formatNumber(item.value) : item.value}
                        </span>
                    </div>
                ))}
            </div>

            {/* Progress indicator */}
            <div className="mt-4 pt-4 border-t border-line-default">
                <div className="h-1 bg-surface-app rounded-full overflow-hidden">
                    <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                            width: '65%',
                            backgroundColor: channelColor 
                        }}
                    />
                </div>
            </div>
        </Link>
    );
};

export const ChannelGrid = ({ linkedinData, emailData, adsData, inboundData }) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ChannelTile channel="linkedin" metrics={linkedinData} />
            <ChannelTile channel="email" metrics={emailData} />
            <ChannelTile channel="ads" metrics={adsData} />
            <ChannelTile channel="inbound" metrics={inboundData} />
        </div>
    );
};

export default ChannelTile;
